import { useFlag } from "@unleash/proxy-client-react";
import { ProductBackendName } from "Components/Features/CheckPrice/Products/productTypes";
import { productBackendNameToInsuranceName } from "Components/Features/CheckPrice/Products/utils";
import { useUserIdentification } from "Components/Features/CheckPrice/userIdentificationHook";
import { TextManager } from "Components/TextContext";
import { PRODUCTS_WITH_SUMMER_DISCOUNT } from "Constants/campaigns";
import { formatToLocaleString } from "Formatting/formatNumber";
import { CalculationPrice } from "Store/priceCalculation/priceCalculationInterfaces";
import { useTexts } from "Store/texts";
import { sum } from "Utils/arrayUtils";
import { isDefined, isNotEmpty } from "Utils/functions";
import { getMonthlyDiscount } from "Utils/priceUtils";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import React, { ReactElement } from "react";
import ReactMarkdown from "react-markdown";
import { PriceDiscountSurface } from "./styles";

export const CustomerDiscounts = ({
  productBackendName,
  prices,
}: {
  productBackendName: ProductBackendName;
  prices: CalculationPrice[];
}): ReactElement => {
  return (
    <PriceDiscountSurface variant="border-alt">
      {isNotEmpty(prices) && <ReasonForDiscountTitle prices={prices} />}
      <ReasonForDiscount productBackendName={productBackendName} />
    </PriceDiscountSurface>
  );
};

const ReasonForDiscountTitle = ({
  prices,
}: {
  prices: CalculationPrice[];
}): ReactElement | null => {
  const discount = prices.map(getMonthlyDiscount).reduce(sum, 0);
  const { getText } = TextManager;
  if (discount <= 0) {
    return null;
  }
  return (
    <ReactMarkdown>
      {getText("common.price.customerdiscount.pricesavedwith", {
        variables: {
          priceText: `**${getText("common.price.permonth", {
            variables: {
              monthlyPrice: formatToLocaleString(discount),
            },
          })}**`,
        },
      })}
    </ReactMarkdown>
  );
};

const ReasonForDiscount = ({
  productBackendName,
}: {
  productBackendName: ProductBackendName;
}): ReactElement | null => {
  const userIdentification = useUserIdentification(productBackendName);
  const isReasonForDiscountEnabled = useFlag(
    "ssx-customer_show-reason-for-discount",
  );

  const fmsTexts = useTexts();
  const user = userIdentification.current;

  if (!user) {
    return null;
  }

  if (!isReasonForDiscountEnabled) {
    return null;
  }

  const agreementProduct = user.discountResponse.f2100.agreementProduct;
  const agreementProductText =
    fmsTexts.domain[`agreementProductDiscount.${agreementProduct}`];
  return (
    <ul style={{ marginLeft: "32px" }}>
      {agreementProductText && agreementProductText !== "" && (
        <li>{agreementProductText}</li>
      )}
      <OnlineDiscount productBackendName={productBackendName} />
    </ul>
  );
};

const OnlineDiscount = ({
  productBackendName,
}: {
  productBackendName: ProductBackendName;
}): React.ReactElement | null => {
  const hasOnlineDiscount = isProductWithOnlineDiscount(productBackendName);
  const isSummerDiscountEnabledForProduct =
    useIsSummerDiscountProduct(productBackendName);
  const { getText } = TextManager;

  if (!hasOnlineDiscount) {
    return null;
  }

  if (isSummerDiscountEnabledForProduct) {
    return <li>{getText("common.price.customerdiscount.summerdiscount")}</li>;
  }

  return <li>{getText("common.price.customerdiscount.onlinediscount")}</li>;
};

const useIsSummerDiscountProduct = (
  productBackendName: ProductBackendName,
): boolean => {
  const summerDiscountFlag = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_815_SUMMER_DISCOUNT,
  );

  if (!summerDiscountFlag) {
    return false;
  }

  const insuranceName = productBackendNameToInsuranceName(productBackendName);
  return (
    isDefined(insuranceName) &&
    PRODUCTS_WITH_SUMMER_DISCOUNT.includes(insuranceName)
  );
};

export const isProductWithOnlineDiscount = (
  productBackendName: ProductBackendName,
): boolean => {
  return !PRODUCTS_WITHOUT_ONLINE_DISCOUNT.includes(productBackendName);
};

const PRODUCTS_WITHOUT_ONLINE_DISCOUNT: ProductBackendName[] = [
  ProductBackendName.AKA_CHILD,
  ProductBackendName.AKA_HEALTH,
  ProductBackendName.CHILD,
  ProductBackendName.HEALTH,
  ProductBackendName.HUSEIERNE_HOUSE_CONTENTS,
  ProductBackendName.CRITICAL_ILLNESS,
  ProductBackendName.DEATH,
  ProductBackendName.DISABILITY,
  ProductBackendName.PREGNANT_AND_NEWBORN,
];
